'use client'
import { useDocumentBundleWizardStore } from '@repo/store/useDocumentBundleWizardStore'
import { useToastStore } from '@repo/store/useToastStore'
import { CircledEmptyCrossIcon } from '@repo/ui/CircledEmptyCrossIcon'
import { confirm as reactConfirm } from '@repo/ui/Confirm'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { DocumentBundleMatterAttachmentsCard } from './DocumentBundleMatterAttachmentsCard'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { FileIcon } from '@repo/ui/FileIcon'
import { formatDate } from '@repo/utils/time'
import { FormSectionTitle } from '@repo/ui/FormSectionTitle'
import { PaperClipIcon } from '@repo/ui/PaperClipIcon'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

type StepProps = {
  wideMode?: boolean
  handlePrevNext: any
  updateStepState: any
  currentStep?: { sectionId?: number | string }
}

const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`

export const DocumentBundleSectionDetails = ({ wideMode, handlePrevNext, updateStepState, currentStep }: StepProps) => {
  const theme = useTheme()
  const {
    sections,
    selectedMatter,
    addNewDocumentsToSection,
    removeNewDocumentFromSection,
    removeUploadedDocumentFromSection,
    fetchUploadedDocumentId,
    addDocumentsToSection,
    deleteDocument
  } = useDocumentBundleWizardStore()
  const section = sections.find((s) => s.id === currentStep?.sectionId)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Existing (already uploaded, from GetBundle) and newly-picked (not yet uploaded) documents are
  // kept distinguishable internally, but shown together in a single list for display purposes.
  const existingDocuments = (section?.documents ?? []).map((document: any) => ({
    key: `existing-${document.matterBundleDocumentId ?? document.name}`,
    name: document.name,
    date: document.createdDate,
    isNew: false,
    fileKey: null as string | null,
    matterBundleDocumentId: document.matterBundleDocumentId ?? null
  }))
  // Documents already sent to AddDocuments this session - the endpoint returns 204 No Content,
  // so this is metadata-only, persisted on the section itself (see addDocumentsToSection) so it
  // survives navigating away from and back to this step.
  const uploadedDocuments = (section?.uploadedDocuments ?? []).map((document: any, index: number) => ({
    key: `uploaded-${index}-${document.name}`,
    name: document.name,
    date: document.date,
    isNew: false,
    isUploadedThisSession: true,
    fileKey: null as string | null,
    matterBundleDocumentId: null as number | string | null
  }))
  const newDocuments = (section?.newAddedDocuments ?? []).map((file: File) => ({
    key: `new-${getFileKey(file)}`,
    name: file.name,
    date: new Date(),
    isNew: true,
    fileKey: getFileKey(file),
    matterBundleDocumentId: null as number | string | null
  }))
  const displayDocuments = [...existingDocuments, ...uploadedDocuments, ...newDocuments]

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 })

  const handleAddDocuments = (files: File[]) => {
    if (!files.length || !section) return

    addNewDocumentsToSection({ sectionId: section.id, files })
  }

  const handleRemoveDocument = (fileKey: string) => {
    if (!section) return

    removeNewDocumentFromSection({ sectionId: section.id, fileKey })
  }

  const handleDeleteDocument = async (documentName: string, matterBundleDocumentId: number | string) => {
    const proceed = await reactConfirm(
      `Are you sure you want to delete "${documentName}"? This action cannot be undone.`
    )
    if (!proceed) return

    try {
      await deleteDocument({ matterBundleDocumentId })
    } catch (err) {
      console.error('Error deleting document:', err)
    }
  }

  // Documents uploaded this session have no known matterBundleDocumentId (AddDocuments returns
  // 204 No Content) - resolve the real id via a read-only lookup, then delete/remove exactly like
  // an already-persisted document.
  const handleDeleteUploadedDocument = async (documentName: string) => {
    if (!section) return

    const proceed = await reactConfirm(
      `Are you sure you want to delete "${documentName}"? This action cannot be undone.`
    )
    if (!proceed) return

    try {
      const matterBundleDocumentId = await fetchUploadedDocumentId({
        matterBundleSectionId: section.matterBundleSectionId,
        documentName
      })

      if (!matterBundleDocumentId) return

      await deleteDocument({ matterBundleDocumentId })
      removeUploadedDocumentFromSection({ sectionId: section.id, documentName })
    } catch (err) {
      console.error('Error deleting document:', err)
    }
  }

  const handleContinue = async () => {
    try {
      const pendingDocuments = section?.newAddedDocuments ?? []

      if (pendingDocuments.length && section?.bundledocsRowKey) {
        await addDocumentsToSection({
          sectionId: section.id,
          sectionRowKey: section.bundledocsRowKey,
          files: pendingDocuments,
          suppressErrorToast: true
        })
      }

      updateStepState({
        status: 'completed',
        statusText: 'Complete'
      })

      handlePrevNext(1)
    } catch (err: any) {
      console.error('Error adding documents to section:', err)

      const message =
        err?.response?.data?.detail || err?.response?.data?.title || err?.response?.data?.message || err?.message

      useToastStore.getState().showToast({
        type: 'error',
        title: 'Error',
        text: message || 'Failed to add documents to this section. Please try again.'
      })
    }
  }
  const documentColumns = [
    {
      field: 'name',
      headerName: 'Document name',
      flex: 1,
      renderCell: (params: any) => <MUITypography variant="body2" sx={{ mb: 0 }}>{params.value}</MUITypography>
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 140,
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
          {formatDate(params.value)}
        </MUITypography>
      )
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) =>
        params.row.isNew && params.row.fileKey ? (
          <MUIIconButton size="small" onClick={() => handleRemoveDocument(params.row.fileKey)}>
            <CircledEmptyCrossIcon sx={{ width: 22, height: 22, color: theme.palette.text.secondary }} />
          </MUIIconButton>
        ) : params.row.isUploadedThisSession ? (
          <MUIIconButton size="small" onClick={() => handleDeleteUploadedDocument(params.row.name)}>
            <CircledEmptyCrossIcon sx={{ width: 22, height: 22, color: theme.palette.text.secondary }} />
          </MUIIconButton>
        ) : !params.row.isNew && params.row.matterBundleDocumentId ? (
          <MUIIconButton
            size="small"
            onClick={() => handleDeleteDocument(params.row.name, params.row.matterBundleDocumentId)}
          >
            <CircledEmptyCrossIcon sx={{ width: 22, height: 22, color: theme.palette.text.secondary }} />
          </MUIIconButton>
        ) : null
    }
  ]

  return (
    <>
      <WizardPanel
        data-testid="DocumentBundleSectionDetailsStep"
        sx={{ backgroundColor: 'transparent', padding: 0 }}
      >
        <MUIStack spacing={5}>
          <MUIPaper
            elevation={0}
            sx={{
              p: 6,
              backgroundColor: 'common.white',
              border: 'none',
              boxShadow: 'none',
              borderRadius: 1
            }}
          >
            <MUIBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DocumentIcon sx={{ width: 32, height: 32 }} />
              <MUITypography variant="h5" sx={{ mb: 0 }}>
                {section?.name ?? 'Section'}
              </MUITypography>
            </MUIBox>
          </MUIPaper>

          <MUIStack
            direction="row"
            spacing={8}
            sx={{
              width: '100%',
              alignItems: 'stretch'
            }}
          >
            <MUIPaper
              elevation={0}
              sx={{
                flex: 3,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                p: 4,
                backgroundColor: 'common.white',
                border: 'none',
                boxShadow: 'none',
                borderRadius: 1
              }}
            >
              <FormSectionTitle title="Section documents" sx={{ mt: 0 }} />

              <MUIStack sx={{ mt: 2, flex: 1 }}>
                <DataGridPro
                  rows={displayDocuments}
                  columns={documentColumns}
                  getRowId={(row: any) => row.key}
                  rowHeight={54}
                  disableColumnSorting
                  disableColumnMenu
                  disableColumnResize
                  disableRowSelectionOnClick
                  pagination
                  pageSizeOptions={[5, 10, 25, 50]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  slots={{
                    noRowsOverlay: () => (
                      <MUIStack sx={{ height: '100%' }} justifyContent="center" alignItems="center">
                        <FileIcon sx={{ width: '3rem', height: '3rem' }} />

                        <MUITypography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                          No documents added to this section.
                        </MUITypography>
                      </MUIStack>
                    )
                  }}
                  sx={{
                    border: 'none',
                    fontSize: '14px',
                    '.MuiDataGrid-columnHeader': { bgcolor: 'action.hover' },
                    '.MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
                    '.MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '.MuiDataGrid-row': { borderBottomColor: 'rgba(5, 28, 51, 0.12)' },
                    '.MuiTablePagination-selectLabel': { display: 'block !important' },
                    '.MuiTablePagination-input': {
                      display: 'inline-flex !important',
                      mb: 0,
                      ml: 0,
                      mt: '8px',
                      fontSize: '0.875rem',
                      '.MuiSvgIcon-root': { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
                    },
                    '.MuiTablePagination-select': {
                      padding: '0 !important',
                      ml: '4px',
                      mr: '20px',
                      minWidth: 'none',
                      [`&:focus`]: { backgroundColor: 'transparent' }
                    },
                    '.MuiTablePagination-actions': { mt: '10px', '& .MuiButtonBase-root': { padding: '0' } }
                  }}
                />
              </MUIStack>
            </MUIPaper>

            <MUIPaper
              elevation={0}
              sx={{
                flex: 2,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                p: 4,
                backgroundColor: 'common.white',
                border: 'none',
                boxShadow: 'none',
                borderRadius: 1
              }}
            >
              <FormSectionTitle
                sx={{ mt: 0 }}
                title="Matter documents"
                endElement={
                  <MUIButton
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                  >
                    <PaperClipIcon sx={{ height: '1rem', width: '1rem', mr: 2 }} />
                    Upload
                  </MUIButton>
                }
              />

              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  handleAddDocuments(Array.from(e.target.files ?? []))
                  if (e.currentTarget) e.currentTarget.value = ''
                }}
              />

              <MUIStack sx={{ mt: 2, flex: 1 }}>
                <DocumentBundleMatterAttachmentsCard
                  clientMatterNo={selectedMatter?.clientMatterNo}
                  sectionId={section?.id}
                />
              </MUIStack>
            </MUIPaper>
          </MUIStack>
        </MUIStack>
      </WizardPanel>

      <WizardFooter
        wideMode={wideMode}
        handleClickBack={() => handlePrevNext(-1)}
        continueButtonText="Continue"
        handleClickContinue={handleContinue}
      />
    </>
  )
}
